'use client';

import Image from 'next/image';
import { cn, parseHeading, sanitizeHtml } from '@/lib/utils';
import Container from '@/components/ui/container';
import { DirectusFile } from '@/types/directus-schema';
import { getDirectusAssetURL } from '@/lib/directus/directus-utils';
import ButtonGroup from '@/components/blocks/ButtonGroup';

interface MultiColumnProps {
	data: {
		background_color?: string;
		title?: string;
		image_size?: 'small' | 'medium' | 'default';
		col_1_enabled?: boolean;
		col_1_image?: DirectusFile;
		col_1_content?: string;
		col_2_enabled?: boolean;
		col_2_image?: DirectusFile;
		col_2_content?: string;
		col_3_enabled?: boolean;
		col_3_image?: DirectusFile;
		col_3_content?: string;
		col_4_enabled?: boolean;
		col_4_image?: DirectusFile;
		col_4_content?: string;
		col_5_enabled?: boolean;
		col_5_image?: DirectusFile;
		col_5_content?: string;
		button_group?: {
			id: string;
			buttons: any[];
		};
	};
}

const MultiColumn = ({ data }: MultiColumnProps) => {
	const {
		background_color = 'white',
		title,
		image_size = 'small',
		col_1_enabled,
		col_1_image,
		col_1_content,
		col_2_enabled,
		col_2_image,
		col_2_content,
		col_3_enabled,
		col_3_image,
		col_3_content,
		col_4_enabled,
		col_4_image,
		col_4_content,
		col_5_enabled,
		col_5_image,
		col_5_content,
		button_group,
	} = data;

	const isLargeImage = image_size === 'default';
	const isMediumImage = image_size === 'medium';

	// Collect active columns
	const columns = [
		{ enabled: col_1_enabled, image: col_1_image, content: col_1_content },
		{ enabled: col_2_enabled, image: col_2_image, content: col_2_content },
		{ enabled: col_3_enabled, image: col_3_image, content: col_3_content },
		{ enabled: col_4_enabled, image: col_4_image, content: col_4_content },
		{ enabled: col_5_enabled, image: col_5_image, content: col_5_content },
	].filter((col) => col.enabled && (col.content || col.image));

	// No content? Render nothing.
	if (columns.length === 0 && !title?.trim()) return null;

	// Background & Text Color Logic
	const getTheme = (bg: string) => {
		// Aggressive overrides for dark themes to ensure white text
		const darkThemeProse = cn(
			'prose-invert',
			'[&_p]:!text-white', // Force paragraphs white
			'[&_h1]:!text-white [&_h2]:!text-white [&_h3]:!text-white [&_h4]:!text-white', // Force headings white
			'[&_strong]:!text-white', // Force bold white
			'[&_li]:!text-white', // Force list items white
			'[&_li::marker]:!text-white', // Force list markers (bullets/numbers) white
			'[&_ol]:!text-white [&_ul]:!text-white', // Force lists white
			'[&_a]:!text-white prose-a:!underline', // Force links white (optional, but good for contrast)
		);

		switch (bg) {
			case 'bright_snow':
				return { bg: 'bg-[#F9FAFB]', text: 'text-gray-900', userProse: 'prose-gray', type: 'light' as const };
			case 'carrot_orange':
				return { bg: 'bg-orange', text: 'text-white', userProse: darkThemeProse, type: 'orange' as const };
			case 'breuer_blue':
				return { bg: 'bg-[#003B64]', text: 'text-white', userProse: darkThemeProse, type: 'blue' as const };
			case 'seaweed':
				return { bg: 'bg-seaweed', text: 'text-white', userProse: darkThemeProse, type: 'seaweed' as const };
			case 'black':
				return { bg: 'bg-black', text: 'text-white', userProse: darkThemeProse, type: 'dark' as const };
			case 'white':
			default:
				return { bg: 'bg-white', text: 'text-gray-900', userProse: 'prose-gray', type: 'light' as const };
		}
	};

	const theme = getTheme(background_color);

	// Grid Logic
	const getGridClass = (count: number) => {
		switch (count) {
			case 2:
				return 'grid-cols-1 md:grid-cols-2';
			case 3:
				return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3';
			case 4:
				return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4';
			case 5:
				return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5';
			default:
				return 'grid-cols-1';
		}
	};

	// Filter valid buttons (handles both M2O object and M2M array relationships)
	const resolvedButtonGroup = Array.isArray(button_group) ? button_group[0]?.block_button_group_id : button_group;

	const validButtons =
		(resolvedButtonGroup?.buttons || (data as any).buttons) // Grouped buttons
			// Direct M2M buttons (fallback)
			?.map((b: any) => b?.block_button_id)
			.filter(Boolean) || [];

	return (
		<section className={cn('py-16 w-full', theme.bg, theme.text)}>
			<Container>
				{title?.trim() && (
					<h2 className={cn('text-h2 font-heading text-center md:text-left', columns.length > 0 ? 'mb-8' : '')}>
						{parseHeading(title)}
					</h2>
				)}

				<div className={cn('grid gap-12 md:gap-20', getGridClass(columns.length))}>
					{columns.map((col, index) => (
						<div key={index} className="space-y-6">
							{/* Column Image */}
							{col.image && (
								<div
									className={cn(
										'relative w-full mb-4',
										!isLargeImage && 'flex justify-center md:justify-start',
									)}
								>
									{isLargeImage ? (
										<div className="relative w-full aspect-[4/3] overflow-hidden rounded-xl">
											<Image
												src={getDirectusAssetURL(col.image)}
												alt={col.image.description || col.image.title || 'Column image'}
												fill
												className="object-cover"
												sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
											/>
										</div>
									) : (
										<div className="relative w-full flex justify-center md:justify-start">
											<Image
												src={getDirectusAssetURL(col.image)}
												alt={col.image.description || col.image.title || 'Column image'}
												width={800}
												height={600}
												className={cn(
													'object-contain w-auto max-w-full',
													isMediumImage ? 'h-[300px]' : 'h-20',
												)}
											/>
										</div>
									)}
								</div>
							)}

							{/* Column Content */}
							{col.content && (
								<div
									className={cn(
										'prose max-w-none text-center md:text-left', // Center on mobile, left on desktop
										theme.userProse,
										// Custom prose overrides to match brand if needed
										'prose-h3:text-h3 prose-h3:font-heading',
									)}
									dangerouslySetInnerHTML={{ __html: sanitizeHtml(col.content || '') }}
								/>
							)}
						</div>
					))}
				</div>

				{/* Button Group */}
				{validButtons.length > 0 && (
					<div className="mt-12 flex justify-center md:justify-start">
						<ButtonGroup buttons={validButtons} theme={theme.type} />
					</div>
				)}
			</Container>
		</section>
	);
};

export default MultiColumn;
